import { ListingBuyerAlertCrudApiClient } from '@steelbuy/api-integration';
import { JsonWebToken } from '@steelbuy/auth';
import { ListingBuyerAlertModel, Nullable } from '@steelbuy/ts-shared';
import {
    createCollectionSlice,
    createSelectCollection,
    createSelectCollectionEntity,
    createSelectCreated,
} from '../../util/store/crud/CollectionSlice';
import { CollectionStore } from '../../util/store/crud/CollectionStore';
import { CollectionStoreAccessors } from '../../util/store/crud/CollectionStoreAccessor';
import {
    createCreateThunk,
    createDeleteThunk,
    createEntityServiceThunk,
    createFetchAllThunk,
    createFetchNextThunk,
    createFetchOneThunk,
    createMutateThunk,
} from '../../util/store/crud/Thunks';

// Basic definition
export const listingBuyerAlertCollectionStoreName = 'listing-buyer-alert-collection-crud';
export type ListingBuyerAlertCollectionStore = {
    [listingBuyerAlertCollectionStoreName]: CollectionStore<ListingBuyerAlertModel>;
};

// Implementation of the async actions
// It is required to declare them before declaring the slice because the block constant has to be defined before using it as the
const createApiClient = (apiBaseUrl: string, jsonWebToken: Nullable<JsonWebToken>) =>
    new ListingBuyerAlertCrudApiClient().init(apiBaseUrl, jsonWebToken);
const fetchAll = createFetchAllThunk<
    ListingBuyerAlertModel,
    ListingBuyerAlertCollectionStore,
    ListingBuyerAlertCrudApiClient
>(listingBuyerAlertCollectionStoreName, createApiClient);
const fetchNext = createFetchNextThunk<
    ListingBuyerAlertModel,
    ListingBuyerAlertCollectionStore,
    ListingBuyerAlertCrudApiClient
>(listingBuyerAlertCollectionStoreName, createApiClient);
const fetchOne = createFetchOneThunk<
    ListingBuyerAlertModel,
    ListingBuyerAlertCollectionStore,
    ListingBuyerAlertCrudApiClient
>(listingBuyerAlertCollectionStoreName, createApiClient);
const mutateEntity = createMutateThunk<ListingBuyerAlertModel, ListingBuyerAlertCrudApiClient>(
    listingBuyerAlertCollectionStoreName,
    createApiClient
);
const createEntity = createCreateThunk<ListingBuyerAlertModel, ListingBuyerAlertCrudApiClient>(
    listingBuyerAlertCollectionStoreName,
    createApiClient
);
const deleteEntity = createDeleteThunk<ListingBuyerAlertModel, ListingBuyerAlertCrudApiClient>(
    listingBuyerAlertCollectionStoreName,
    createApiClient
);
const entityService = createEntityServiceThunk<ListingBuyerAlertModel, ListingBuyerAlertCrudApiClient>(
    listingBuyerAlertCollectionStoreName,
    createApiClient
);

// Slice definition
export const listingBuyerAlertCollectionSlice = createCollectionSlice<ListingBuyerAlertModel>(
    listingBuyerAlertCollectionStoreName,
    fetchAll,
    fetchNext,
    fetchOne,
    mutateEntity,
    createEntity,
    deleteEntity,
    entityService
);
const { resetFetchStatus, resetActionStatus } = listingBuyerAlertCollectionSlice.actions;

// Selector functions to be used with useSelector or useTypedSelector to read from the state
const selectCollection = createSelectCollection<ListingBuyerAlertModel, ListingBuyerAlertCollectionStore>(
    listingBuyerAlertCollectionStoreName
);
const selectEntity = createSelectCollectionEntity<ListingBuyerAlertModel, ListingBuyerAlertCollectionStore>(
    listingBuyerAlertCollectionStoreName
);
const selectCreated = createSelectCreated<ListingBuyerAlertModel, ListingBuyerAlertCollectionStore>(
    listingBuyerAlertCollectionStoreName
);

// Export the store accessors to be used by the data provider value
export const listingBuyerAlertCollectionStoreAccessors = {
    fetchAll,
    fetchNext,
    fetchOne,
    createEntity,
    mutateEntity,
    deleteEntity,
    resetFetchStatus,
    resetActionStatus,
    selectCollection,
    selectEntity,
    selectCreated,
    entityService,
} as CollectionStoreAccessors<ListingBuyerAlertModel, ListingBuyerAlertCollectionStore>;
